export function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatAverage(value) {
  if (value === null || value === undefined) return null;
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export function formatScore(value) {
  if (value === null || value === undefined) return null;
  const rounded = Math.round(Number(value));
  return Number.isFinite(rounded) ? String(rounded) : null;
}

export function formatScoreOutOf100(value) {
  const score = formatScore(value);
  return score === null ? null : `${score}/100`;
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function formatRelativeAge(value) {
  if (!value) return null;
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return null;
  const diff = Date.now() - ts;
  if (diff < 0) return "just now";
  if (diff < 60_000) return "just now";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function parseLessonId(value) {
  if (!value) return { week: null, lesson: null };
  const match = String(value).trim().match(/^W(\d+)L(\d+)$/i);
  if (!match) return { week: null, lesson: null };
  return {
    week: Number(match[1]) || null,
    lesson: Number(match[2]) || null,
  };
}

function normalizeAttentionReason(reason, curriculumMap = new Map()) {
  const lessonId = String(reason?.lesson_id || "").trim().toUpperCase() || null;
  const lessonIdParts = parseLessonId(lessonId);
  const week = Number(reason?.week ?? lessonIdParts.week) || null;
  const lesson = Number(reason?.lesson ?? lessonIdParts.lesson) || null;
  const mappedLesson = lessonId ? curriculumMap.get(lessonId) : null;
  const lessonTitle =
    reason?.lesson_title ||
    mappedLesson?.title ||
    mappedLesson?.topic ||
    (lessonId ? lessonId.replace(/^W/i, "W") : null) ||
    null;
  const score = safeNumber(reason?.score);
  const createdAt = reason?.created_at || null;

  return {
    type: reason?.type || "struggling",
    label: reason?.label || "Needs review",
    priority: Number(reason?.priority ?? 99) || 99,
    lesson_id: lessonId || (week !== null && lesson !== null ? `W${week}L${lesson}` : null),
    lesson_title: lessonTitle,
    score,
    score_label: formatScore(score),
    created_at: createdAt,
    age_label: formatRelativeAge(createdAt),
    week,
    lesson,
  };
}

function normalizeReviewQueueItem(item, curriculumMap = new Map()) {
  const reasons = Array.isArray(item?.attention_reasons)
    ? item.attention_reasons.map((reason) => normalizeAttentionReason(reason, curriculumMap))
    : [];
  reasons.sort((a, b) => {
    if ((a.priority || 99) !== (b.priority || 99)) return (a.priority || 99) - (b.priority || 99);
    const aTime = new Date(a.created_at || 0).getTime();
    const bTime = new Date(b.created_at || 0).getTime();
    if (aTime !== bTime) return aTime - bTime;
    return String(a.lesson_id || "").localeCompare(String(b.lesson_id || ""));
  });

  const primaryReason = normalizeAttentionReason(item?.primary_reason || reasons[0] || {}, curriculumMap);
  const score = safeNumber(item?.score ?? primaryReason.score);
  const latestLessonId = String(item?.latest_lesson_id || primaryReason.lesson_id || "").trim().toUpperCase() || null;
  const latestLessonMeta = latestLessonId ? curriculumMap.get(latestLessonId) : null;
  const latestLessonTitle =
    item?.latest_lesson_title ||
    latestLessonMeta?.title ||
    latestLessonMeta?.topic ||
    primaryReason.lesson_title ||
    null;

  return {
    student_id: item?.student_id,
    student_name: item?.student_name || item?.student_id,
    latest_lesson_id: latestLessonId,
    latest_lesson_title: latestLessonTitle,
    score,
    score_label: item?.score_label || formatScore(score) || null,
    attention_count: Number(item?.attention_count ?? reasons.length) || reasons.length,
    attention_reasons: reasons,
    primary_reason: primaryReason,
    priority: Number(item?.priority ?? primaryReason.priority ?? 99) || 99,
    created_at: item?.created_at || primaryReason.created_at || null,
    latest_activity: item?.latest_activity || item?.created_at || null,
  };
}

function resolveLessonSubmissionScore(submission) {
  const feedback = submission?.feedback || null;
  const resultJson = parseMaybeJson(submission?.result_json) || {};
  return (
    safeNumber(resultJson?.score) ??
    safeNumber(resultJson?.overall) ??
    safeNumber(resultJson?.rubric_score) ??
    safeNumber(submission?.score) ??
    safeNumber(feedback?.score) ??
    safeNumber(feedback?.overall_score) ??
    safeNumber(feedback?.rubric_score) ??
    safeNumber(resultJson?.overall_score) ??
    safeNumber(resultJson?.rubric_score) ??
    safeNumber(submission?.overall_score) ??
    safeNumber(submission?.rubric_score) ??
    null
  );
}

export function buildCurriculumLessonMap(curriculum) {
  const map = new Map();
  const weeks = Array.isArray(curriculum?.weeks) ? curriculum.weeks : [];
  for (const week of weeks) {
    const weekNum = Number(week?.week) || 0;
    const lessons = Array.isArray(week?.lessons) ? week.lessons : [];
    for (const lesson of lessons) {
      const lessonNum = Number(lesson?.lesson) || 0;
      const lessonId = String(lesson?.id || `W${weekNum}L${lessonNum}`).trim().toUpperCase();
      map.set(lessonId, {
        ...lesson,
        week: Number(lesson?.week ?? weekNum) || weekNum,
        lesson: Number(lesson?.lesson ?? lessonNum) || lessonNum,
        id: lessonId,
      });
    }
  }
  return map;
}

export function deriveSubmissionReview(submission) {
  const feedback = submission?.feedback || null;
  const tutorNote = submission?.tutor_note || null;
  const score = resolveLessonSubmissionScore(submission);
  const scores = {
    creativity_score: safeNumber(feedback?.creativity_score),
    clarity_score: safeNumber(feedback?.clarity_score),
    task_score: safeNumber(feedback?.task_score),
  };
  const missingFeedback = Boolean(submission && !feedback);

  return {
    submission_id: submission?.submission_id,
    week: submission?.week ?? null,
    lesson: submission?.lesson ?? null,
    created_at: submission?.created_at || null,
    draft_text: submission?.draft_text || "",
    final_text: submission?.final_text || "",
    feedback,
    tutor_note: tutorNote,
    reviewed: Boolean(tutorNote),
    score,
    score_label: formatScore(score),
    scores,
    average_score: null,
    average_score_label: null,
    needs_review: !tutorNote && missingFeedback,
  };
}

export function deriveTutorStudent(student, curriculumMap = new Map()) {
  const submissions = [...(student.submissions || [])].sort((a, b) => {
    const bDate = new Date(b.created_at || 0).getTime();
    const aDate = new Date(a.created_at || 0).getTime();
    if (bDate !== aDate) return bDate - aDate;
    return Number(b.submission_id || 0) - Number(a.submission_id || 0);
  });

  const latestSubmission = submissions[0] || null;
  const latestReview = deriveSubmissionReview(latestSubmission);
  const latestByLesson = [];
  const seenLessons = new Set();

  submissions.forEach((submission) => {
    const key = `${submission.week}:${submission.lesson}`;
    if (seenLessons.has(key)) return;
    seenLessons.add(key);
    latestByLesson.push(deriveSubmissionReview(submission));
  });

  const reviewItems = Array.isArray(student.review_items)
    ? student.review_items
    : latestByLesson.filter((item) => item.needs_review);
  const needsReview = student.needs_review !== undefined
    ? Boolean(student.needs_review)
    : reviewItems.length > 0;

  const completedLessons = new Set(
    submissions.map((submission) => `${Number(submission.week)}:${Number(submission.lesson)}`)
  );
  const completedScores = submissions
    .filter((submission) => !submission.status || ["completed", "passed", "closed"].includes(String(submission.status).toLowerCase()))
    .map((submission) => resolveLessonSubmissionScore(submission))
    .filter((score) => score !== null);
  const bestScore = student.best_score ?? (completedScores.length ? Math.max(...completedScores) : null);
  const averageScore = student.average_score ?? (completedScores.length
    ? completedScores.reduce((total, score) => total + score, 0) / completedScores.length
    : null);
  const currentLessonId = student.current_week && student.current_lesson
    ? `W${student.current_week}L${student.current_lesson}`
    : null;

  const latestLessonId = latestSubmission ? `W${latestSubmission.week}L${latestSubmission.lesson}` : null;
  const latestLessonMeta = latestLessonId ? curriculumMap.get(String(latestLessonId).toUpperCase()) : null;

  return {
    ...student,
    submissions,
    review_items: reviewItems,
    latest_submission: latestSubmission,
    latest_week: latestSubmission?.week ?? null,
    latest_lesson: latestSubmission?.lesson ?? null,
    latest_activity: latestSubmission?.created_at || null,
    latestFeedback: latestReview.feedback,
    latest_score: student.latest_score ?? latestReview.score,
    latest_score_label: latestReview.score_label,
    latestScores: latestReview.scores,
    average_score: averageScore,
    average_score_label: student.average_score_label || formatScoreOutOf100(averageScore),
    best_score: bestScore,
    best_score_label: student.best_score_label || formatScoreOutOf100(bestScore),
    completed_lessons: completedLessons.size,
    total_lessons: curriculumMap.size || 16,
    current_week: student.current_week ?? null,
    current_lesson: student.current_lesson ?? null,
    current_lesson_id: currentLessonId,
    latest_lesson_id: latestLessonId,
    latest_lesson_title: latestLessonMeta?.title || latestLessonMeta?.topic || latestLessonId || "No recent lesson",
    needs_review: needsReview,
    latest_reviewed: Boolean(latestReview.reviewed),
    review_status: student.review_status || (latestSubmission
      ? latestReview.reviewed
        ? "Reviewed"
        : needsReview
        ? reviewItems.length > 1
          ? `${reviewItems.length} to review`
          : "Needs review"
        : "On track"
      : "No submissions yet"),
    status_summary: latestSubmission
      ? needsReview
        ? "Needs review"
        : "On track"
      : "No submissions yet",
  };
}

export function buildNeedsReviewQueue(students, curriculumMap = new Map()) {
  const source = Array.isArray(students) ? students : [];
  if (source.length && (source[0]?.attention_reasons || source[0]?.attention_count)) {
    return source
      .map((item) => normalizeReviewQueueItem(item, curriculumMap))
      .filter((item) => item.attention_count > 0)
      .sort((a, b) => {
        if ((a.priority || 99) !== (b.priority || 99)) return (a.priority || 99) - (b.priority || 99);
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        if (aTime !== bTime) return aTime - bTime;
        return String(a.student_name || "").localeCompare(String(b.student_name || ""));
      });
  }

  const queue = [];
  for (const student of source) {
    const normalized = deriveTutorStudent(student, curriculumMap);
    for (const item of normalized.review_items || []) {
      const lessonId = `W${item.week}L${item.lesson}`;
      const lessonMeta = curriculumMap.get(String(lessonId).toUpperCase()) || {};
      const submission = (normalized.submissions || []).find(
        (sub) => Number(sub.week) === Number(item.week) && Number(sub.lesson) === Number(item.lesson)
      );
      const submissionText = String(submission?.draft_text || submission?.final_text || "").trim();
      queue.push({
        student_id: normalized.student_id,
        student_name: normalized.name || normalized.student_id,
        week: item.week,
        lesson: item.lesson,
        lesson_id: lessonId,
        lesson_title: lessonMeta.title || lessonMeta.topic || item.lesson || `Lesson ${item.lesson}`,
        submitted_at: item.created_at || normalized.latest_activity || "",
        score: item.score,
        score_label: item.score_label || formatScore(item.score) || item.average_score_label || null,
        overall_score: item.score,
        rubric_score: item.score,
        average_score_label: item.average_score_label || "Needs review",
        scores: item.scores,
        preview: submissionText ? submissionText.slice(0, 140) : "",
        submission_id: item.submission_id,
        status: normalized.review_status,
        needs_review: true,
        latest_activity: normalized.latest_activity,
      });
    }
  }

  return queue.sort((a, b) => {
    const aTime = new Date(a.submitted_at || 0).getTime();
    const bTime = new Date(b.submitted_at || 0).getTime();
    return bTime - aTime;
  });
}

const LESSON_INACTIVITY_DAYS = 4;
const EXCEPTIONAL_SCORE_THRESHOLD = 80;
const PASS_SCORE_THRESHOLD = 70;

function parseTimeValue(value) {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function lessonKey(week, lesson) {
  const weekNum = Number(week);
  const lessonNum = Number(lesson);
  if (!Number.isFinite(weekNum) || !Number.isFinite(lessonNum)) return "";
  return `${weekNum}:${lessonNum}`;
}

function lessonId(week, lesson) {
  const weekNum = Number(week);
  const lessonNum = Number(lesson);
  if (!Number.isFinite(weekNum) || !Number.isFinite(lessonNum)) return null;
  return `W${weekNum}L${lessonNum}`;
}

function lessonSortValue(week, lesson) {
  const weekNum = Number(week);
  const lessonNum = Number(lesson);
  return [
    Number.isFinite(weekNum) ? weekNum : 10**9,
    Number.isFinite(lessonNum) ? lessonNum : 10**9,
  ];
}

function lessonMetaFromMap(curriculumMap, week, lesson) {
  const id = lessonId(week, lesson);
  return id ? curriculumMap.get(id) || null : null;
}

function resolveLessonPassScore(submission) {
  const resultJson = parseMaybeJson(submission?.result_json) || {};
  const passScore =
    safeNumber(resultJson?.pass_score) ??
    safeNumber(submission?.pass_score) ??
    safeNumber(submission?.feedback?.pass_score) ??
    PASS_SCORE_THRESHOLD;
  return passScore;
}

function truthyHelpRequest(payload) {
  if (!payload || typeof payload !== "object") return false;
  const helpKeys = new Set([
    "tutor_help_requested",
    "help_requested",
    "help_request",
    "request_tutor_help",
    "need_help",
    "needs_help",
  ]);
  for (const [key, value] of Object.entries(payload)) {
    if (helpKeys.has(String(key || "").trim().toLowerCase()) && Boolean(value)) {
      return true;
    }
    if (value && typeof value === "object" && truthyHelpRequest(value)) {
      return true;
    }
  }
  return false;
}

function hasHelpRequest(submission) {
  const resultJson = parseMaybeJson(submission?.result_json) || {};
  const flagsJson = parseMaybeJson(submission?.flags_json) || {};
  return truthyHelpRequest(resultJson) || truthyHelpRequest(flagsJson);
}

function isLessonCompleted(submission, score, passScore) {
  const resultJson = parseMaybeJson(submission?.result_json) || {};
  const status = String(submission?.status || resultJson?.status || "").trim().toLowerCase();
  if (["completed", "passed", "closed"].includes(status)) return true;
  if (resultJson?.pass_recommendation === true) return true;
  return score !== null && score >= passScore;
}

function formatLessonFocus(meta) {
  const raw = String(meta?.objective || meta?.lesson_focus || meta?.focus || meta?.title || meta?.topic || "").trim();
  if (!raw) return "";
  return raw.replace(/\.$/, "");
}

function formatReasonSentence(item) {
  const lessonLabel = item.lesson_id || "the lesson";
  const scoreText = item.score_label ? `scored ${item.score_label}` : "submitted work";
  const focus = formatLessonFocus(item.lesson_meta);
  const focusSuffix = focus ? ` on ${focus.toLowerCase()}` : "";

  if (item.reason_type === "help_request") {
    return `${item.student_name} requested tutor help on ${lessonLabel}${focusSuffix}, and the latest submission ${scoreText}.`;
  }

  if (item.reason_type === "failed_attempts") {
    return `${item.student_name} struggled with ${lessonLabel}${focusSuffix} across ${item.attempt_count} attempts and may benefit from targeted guidance.`;
  }

  if (item.reason_type === "score_drop") {
    return `Performance dropped from ${item.previous_score_label} in ${item.previous_lesson_id} to ${item.score_label} in ${lessonLabel}, suggesting difficulty transferring earlier control.`;
  }

  if (item.reason_type === "inactivity") {
    return `Completed with ${item.score_label}, but has been inactive for ${item.age_label} since submission.`;
  }

  if (item.reason_type === "exceptional") {
    return `Strong ${lessonLabel} submission with ${item.score_label} and clear control of the lesson focus${focus ? ` (${focus.toLowerCase()})` : ""}.`;
  }

  return `${item.student_name} needs tutor attention on ${lessonLabel}.`;
}

export function formatTriageAgeLabel(value) {
  const ts = parseTimeValue(value);
  if (ts === null) return null;
  const diff = Math.max(0, Date.now() - ts);
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`;
  if (diff < 86_400_000) return `${Math.max(1, Math.floor(diff / 3_600_000))}h ago`;
  const days = Math.max(1, Math.floor(diff / 86_400_000));
  if (days === 1) return "Yesterday";
  return `${days} days`;
}

export function buildNeedsReviewLessonRows(students, curriculumMap = new Map()) {
  const source = Array.isArray(students) ? students : [];
  const rows = [];

  for (const student of source) {
    const submissions = Array.isArray(student?.submissions) ? [...student.submissions] : [];
    if (!submissions.length) continue;

    submissions.sort((a, b) => {
      const bTime = parseTimeValue(b?.created_at) ?? 0;
      const aTime = parseTimeValue(a?.created_at) ?? 0;
      if (bTime !== aTime) return bTime - aTime;
      return Number(b?.submission_id || 0) - Number(a?.submission_id || 0);
    });

    const lessonGroups = new Map();
    for (const submission of submissions) {
      const weekNum = Number(submission?.week);
      const lessonNum = Number(submission?.lesson);
      if (!Number.isFinite(weekNum) || !Number.isFinite(lessonNum)) continue;
      const key = lessonKey(weekNum, lessonNum);
      if (!lessonGroups.has(key)) lessonGroups.set(key, []);
      lessonGroups.get(key).push(submission);
    }

    const sortedKeys = [...lessonGroups.keys()].sort((a, b) => {
      const [aWeek, aLesson] = a.split(":").map((value) => Number(value));
      const [bWeek, bLesson] = b.split(":").map((value) => Number(value));
      const [weekDiff, lessonDiff] = lessonSortValue(aWeek, aLesson);
      const [otherWeekDiff, otherLessonDiff] = lessonSortValue(bWeek, bLesson);
      if (weekDiff !== otherWeekDiff) return weekDiff - otherWeekDiff;
      return lessonDiff - otherLessonDiff;
    });

    let previousScoredLesson = null;
    for (const key of sortedKeys) {
      const lessonSubs = lessonGroups.get(key) || [];
      const latestSubmission = lessonSubs[0] || null;
      const weekNum = Number(latestSubmission?.week);
      const lessonNum = Number(latestSubmission?.lesson);
      const lesson_id = lessonId(weekNum, lessonNum);
      const lesson_meta = lessonMetaFromMap(curriculumMap, weekNum, lessonNum);
      const score = resolveLessonSubmissionScore(latestSubmission);
      const passScore = resolveLessonPassScore(latestSubmission);
      const attemptCount = lessonSubs.length;
      const attemptsFailed = lessonSubs.filter((submission) => {
        const attemptScore = resolveLessonSubmissionScore(submission);
        return attemptScore !== null && attemptScore < passScore;
      }).length;
      const hasTutorNote = lessonSubs.some((submission) => Boolean(submission?.tutor_note));
      const helpRequested = lessonSubs.some((submission) => hasHelpRequest(submission));
      const latestActivity = latestSubmission?.created_at || null;
      const age_label = formatTriageAgeLabel(latestActivity);
      const age_ms = parseTimeValue(latestActivity);
      const completed = isLessonCompleted(latestSubmission, score, passScore);
      const scoreDrop =
        previousScoredLesson &&
        previousScoredLesson.score !== null &&
        score !== null &&
        (previousScoredLesson.score - score) >= 15;
      const inactive = age_ms !== null && Math.max(0, Date.now() - age_ms) >= LESSON_INACTIVITY_DAYS * 86_400_000;

      let reason_type = null;
      let priority = null;
      let action_label = "Review";
      let previous_score_label = previousScoredLesson?.score_label || null;
      let previous_lesson_id = previousScoredLesson?.lesson_id || null;

      if (helpRequested) {
        reason_type = "help_request";
        priority = 1;
        action_label = "Reply";
      } else if (attemptCount >= 2 && attemptsFailed >= 2 && score !== null && score < passScore) {
        reason_type = "failed_attempts";
        priority = 2;
      } else if (scoreDrop) {
        reason_type = "score_drop";
        priority = 3;
      } else if (inactive && completed) {
        reason_type = "inactivity";
        priority = 4;
        action_label = "Review";
      } else if (score !== null && score >= EXCEPTIONAL_SCORE_THRESHOLD) {
        reason_type = "exceptional";
        priority = 5;
      }

      if (hasTutorNote) {
        action_label = "Done";
      }

      if (!reason_type) {
        if (score !== null) {
          previousScoredLesson = {
            lesson_id,
            score,
            score_label: formatScore(score),
          };
        }
        continue;
      }

      const row = {
        student_id: student?.student_id,
        student_name: student?.name || student?.student_name || student?.student_id,
        lesson_id,
        lesson_title: lesson_meta?.title || lesson_meta?.topic || lesson_id || "Lesson",
        lesson_objective: lesson_meta?.objective || lesson_meta?.task || lesson_meta?.success_criteria || "",
        lesson_meta,
        score,
        score_label: formatScore(score) || null,
        age_label,
        age_ms,
        priority,
        reason_type,
        reason: "",
        action_label,
        attempt_count: attemptCount,
        previous_score_label,
        previous_lesson_id,
        week: weekNum,
        lesson: lessonNum,
        created_at: latestActivity,
      };
      row.reason = formatReasonSentence(row);
      rows.push(row);

      if (score !== null) {
        previousScoredLesson = {
          lesson_id,
          score,
          score_label: formatScore(score),
        };
      }
    }
  }

  return rows.sort((a, b) => {
    if ((a.priority || 99) !== (b.priority || 99)) return (a.priority || 99) - (b.priority || 99);
    const aTime = a.age_ms ?? 0;
    const bTime = b.age_ms ?? 0;
    if (aTime !== bTime) return bTime - aTime;
    if ((a.week || 0) !== (b.week || 0)) return (a.week || 0) - (b.week || 0);
    if ((a.lesson || 0) !== (b.lesson || 0)) return (a.lesson || 0) - (b.lesson || 0);
    return String(a.student_name || "").localeCompare(String(b.student_name || ""));
  });
}

function formatGroupReasonSummary(item, lessonCount) {
  if (!item) return `${lessonCount} lessons need attention.`;
  const countText = `${lessonCount} lesson${lessonCount === 1 ? "" : "s"} need attention`;
  if (item.reason_type === "help_request") {
    return `${countText}; latest issue is a tutor help request after ${item.lesson_id}.`;
  }
  if (item.reason_type === "failed_attempts") {
    return `${countText}; latest issue is two failed attempts on ${item.lesson_id}.`;
  }
  if (item.reason_type === "score_drop") {
    return `${countText}; latest issue is a score drop on ${item.lesson_id}.`;
  }
  if (item.reason_type === "inactivity") {
    return `${countText}; latest issue is inactivity after ${item.lesson_id}.`;
  }
  if (item.reason_type === "exceptional") {
    return `${countText}; latest issue is a strong submission in ${item.lesson_id}.`;
  }
  return `${countText}; latest issue is ${item.lesson_id}.`;
}

function sortLessonRowsForStudent(a, b) {
  if ((a.priority || 99) !== (b.priority || 99)) return (a.priority || 99) - (b.priority || 99);
  const aTime = a.age_ms ?? 0;
  const bTime = b.age_ms ?? 0;
  if (aTime !== bTime) return bTime - aTime;
  if ((a.week || 0) !== (b.week || 0)) return (a.week || 0) - (b.week || 0);
  if ((a.lesson || 0) !== (b.lesson || 0)) return (a.lesson || 0) - (b.lesson || 0);
  return String(a.lesson_id || "").localeCompare(String(b.lesson_id || ""));
}

export function buildNeedsReviewStudentGroups(students, curriculumMap = new Map()) {
  const lessonRows = buildNeedsReviewLessonRows(students, curriculumMap);
  const groups = new Map();

  for (const row of lessonRows) {
    const key = String(row.student_id || "").trim();
    if (!key) continue;

    if (!groups.has(key)) {
      groups.set(key, {
        student_id: row.student_id,
        student_name: row.student_name,
        lesson_count: 0,
        latest_lesson_id: null,
        latest_lesson_title: null,
        latest_activity: null,
        latest_activity_ms: null,
        priority: row.priority ?? 99,
        highest_priority_action: row.action_label || "Review",
        highest_priority_reason: "",
        highest_priority_item: row,
        lessons: [],
      });
    }

    const group = groups.get(key);
    group.lessons.push(row);
    group.lesson_count += 1;

    const rowActivityMs = row.age_ms ?? null;
    if (rowActivityMs !== null && (group.latest_activity_ms === null || rowActivityMs > group.latest_activity_ms)) {
      group.latest_activity_ms = rowActivityMs;
      group.latest_activity = row.created_at || null;
      group.latest_lesson_id = row.lesson_id || null;
      group.latest_lesson_title = row.lesson_title || null;
    }

    const primary = group.highest_priority_item;
    const rowIsHigherPriority =
      !primary ||
      (row.priority || 99) < (primary.priority || 99) ||
      ((row.priority || 99) === (primary.priority || 99) &&
        (row.age_ms ?? 0) > (primary.age_ms ?? 0));
    if (rowIsHigherPriority) {
      group.priority = row.priority ?? group.priority ?? 99;
      group.highest_priority_item = row;
      group.highest_priority_action = row.action_label || group.highest_priority_action || "Review";
      group.highest_priority_reason = formatGroupReasonSummary(row, group.lesson_count);
    } else if (!group.highest_priority_reason) {
      group.highest_priority_reason = formatGroupReasonSummary(primary, group.lesson_count);
    } else {
      group.highest_priority_reason = formatGroupReasonSummary(group.highest_priority_item, group.lesson_count);
    }
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      lessons: group.lessons.sort(sortLessonRowsForStudent),
      lesson_count_label: `${group.lesson_count} lesson${group.lesson_count === 1 ? "" : "s"} need review`,
      latest_lesson_label: group.latest_lesson_id || "—",
    }))
    .sort((a, b) => {
      if ((a.priority || 99) !== (b.priority || 99)) return (a.priority || 99) - (b.priority || 99);
      const aTime = a.latest_activity_ms ?? 0;
      const bTime = b.latest_activity_ms ?? 0;
      if (aTime !== bTime) return bTime - aTime;
      return String(a.student_name || "").localeCompare(String(b.student_name || ""));
    });
}
